import { MediaModel } from "app/services/api/types/common/models/MediaModel";
import { DocumentPickerAsset } from "expo-document-picker";
import { useEffect, useMemo, useRef, useState } from "react";
import { View } from "ui/atoms/view";
import { Linking, Pressable, Text } from "react-native";
import { Icon, PaperclipIcon } from "ui/gluestack/icon";
import { getFileExtension } from "ui/utils/fileUtils";
import { ImagePreviewModal } from "ui/organisms/modals/ImagePreviewModal";
import { Image } from "expo-image";
import { isMediaModel } from "ui/types/ThrelsFileType";
import { tva, VariantProps } from "@gluestack-ui/nativewind-utils";

const viewDocumentStyle = tva({
  base: "aspect-square border border-gray-200 rounded-xl overflow-hidden",
  variants: {
    size: {
      default: "w-[286px] h-[260px]",
      xs: "w-[200px] h-[200px]",
    },
  },
});

type Props = {
  document: MediaModel | DocumentPickerAsset;
} & VariantProps<typeof viewDocumentStyle>;

export function ViewDocument({ document, size = "default" }: Props) {
  const [imageLoadingError, setImageLoadingError] = useState(false);

  const imagePreviewRef =
    useRef<React.ComponentRef<typeof ImagePreviewModal>>(null);

  const url = useMemo(() => {
    if (!document) return undefined;
    return isMediaModel(document) ? document.url : document.uri;
  }, [document]);

  const name = useMemo(() => {
    if (!document) return undefined;
    return isMediaModel(document) ? document.file_name : document.name;
  }, [document]);

  const extension = useMemo(() => {
    return getFileExtension(name) || getFileExtension(url);
  }, [url]);

  const isImage = useMemo(() => {
    if (!extension) return false;
    return ["jpg", "jpeg", "png", "gif", "webp"].includes(extension);
  }, [extension]);

  const isPdf = useMemo(() => {
    return extension === "pdf";
  }, [extension]);

  useEffect(() => {
    setImageLoadingError(false);
  }, [url]);

  const openUrl = () => {
    if (url)
      Linking.openURL(url).catch((err) => {
        console.error("An error occurred", err);
      });
  };

  return (
    <View className={viewDocumentStyle({ size })}>
      {isImage && !imageLoadingError ? (
        <Pressable
          onPress={() => url && imagePreviewRef.current?.show(url)}
          className="w-full h-full p-1"
        >
          <Image
            source={{ uri: url }}
            className="w-full h-full"
            contentFit="contain"
            onError={() => setImageLoadingError(true)}
          />
        </Pressable>
      ) : (
        <Pressable
          onPress={openUrl}
          className="flex flex-1 flex-col items-center justify-center gap-4 p-4 bg-gray-200"
        >
          <Icon as={PaperclipIcon} className="text-primary-500" />
          <Text className="text-center max-w-full">{name}</Text>
        </Pressable>
      )}

      <ImagePreviewModal ref={imagePreviewRef} />
    </View>
  );
}
